/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ExerciseHelpButton } from './ExerciseHelp';
import { useProgramProgressFeature } from '../hooks/use-progress-tracker';

export const Sidebar = ({
  isOpen,
  onClose,
  width = 480,
  children,
  portalTarget = '#tutor-sider', // CSS selector string for desktop portal target
  desktopClassName = '',
}: {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  width?: number;
  portalTarget?: string; // CSS selector for the portal target element
  desktopClassName?: string;
}) => {
  const { enabled: showingProgressTracker } = useProgramProgressFeature();
  const [isMobile, setIsMobile] = useState(false);
  const [portalElement, setPortalElement] = useState<Element | null>(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 800);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // Prevent body scroll when mobile sidebar is open
    const overflow = isMobile && isOpen ? 'hidden' : 'auto';
    document.body.style.overflow = overflow;
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isMobile, isOpen]);

  useEffect(() => {
    if (portalTarget && !isMobile) {
      setPortalElement(document.querySelector(portalTarget));
    } else {
      setPortalElement(null);
    }
  }, [portalTarget, isMobile]);

  const sidebarContent = (
    <div
      className={`
    bg-warm-5 transition-all duration-300 ease-in-out pb-1
    ${
      isMobile
        ? ` fixed top-0 right-0 h-screen w-full transform flex flex-col
            ${isOpen ? 'translate-x-0' : 'translate-x-full'} `
        : ` h-full flex flex-col ${isOpen ? 'block' : 'hidden'} ${desktopClassName}`
    }
  `}
      style={{
        zIndex: 1002,
        width: width,
        borderRadius: isMobile ? undefined : 5,
        height: isMobile
          ? '100vh'
          : document.body.clientHeight - (showingProgressTracker ? 250 : 150),
        maxHeight: isMobile
          ? '100vh'
          : document.body.clientHeight - (showingProgressTracker ? 250 : 150),
        overflow: 'hidden',
      }}
    >
      <div className="bg-warm-30/30 flex items-center justify-between px-1 py-3 flex-shrink-0">
        <ExerciseHelpButton isOpen onClick={onClose} />
      </div>
      <div className="flex-1 overflow-y-auto p-8 pt-8" style={{ maxWidth: width }}>
        {children}
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        {isOpen && (
          <div
            role="none"
            className="fixed inset-0 bg-black/20 bg-opacity-50 transition-opacity duration-300"
            style={{ zIndex: 1001 }}
            onClick={onClose}
            onKeyUp={(e) => {
              if (e.key === 'Escape') {
                onClose();
              }
            }}
          />
        )}
        {sidebarContent}
      </>
    );
  }

  if (portalElement && !isMobile) {
    return createPortal(sidebarContent, portalElement);
  }

  if (!portalElement && !isMobile)
    console.log('Sidebar portal target not found or mobile view active.');

  return null;
};
